import React, { useMemo, useRef } from 'react';
import { View, Pressable, Keyboard, Platform, StyleSheet } from 'react-native';
import { FastField, Field, FieldProps } from 'formik';
import AnimatedLottieView from '@utils/lottieAnimation';
import HandleFieldType from './handleFieldType';
import ErrorBoundary from '@utils/ErrorBoundary';
import { Label, Text, Separator, YStack, XStack } from 'tamagui';
import parseByDelimiter from '../utils/parseByDelimiter';

export const GetField = React.memo(({ blackText, hideLabel, otherVals, item, field, allItems, index, nextFieldFunction, setInputRef, fieldProps, readOnly, zIndex, inlineFields }) => {
    // Use stable dependencies - don't include 'field' as it changes on every keystroke
    // Only include item and fieldProps.meta for error/touched state
    
    // Check if this is a dropdown field
    const DROPDOWN_TYPES = [
      'select', 'picker', 'dropdown', 'dropDown', 'combobox', 
      'date', 'time', 'datetime', 'user_select', 'user_stores',
      'userSelect', 'userStores'
    ];
    const refOrKey = item.ref || item.key || '';
    const isDropdown = DROPDOWN_TYPES.includes(item.type) || 
                       DROPDOWN_TYPES.includes(item.content?.type) ||
                       DROPDOWN_TYPES.includes(item.ref) ||
                       DROPDOWN_TYPES.includes(item.key) ||
                       (refOrKey && (
                         refOrKey.includes('user') || 
                         refOrKey.includes('select') || 
                         refOrKey.includes('dropdown') ||
                         refOrKey.includes('stores')
                       ));
    
    return (
      <YStack 
        minHeight={Platform.select({ web: 80, default: undefined })} 
        flex={1}
        width={inlineFields ? '100%' : undefined}
        zIndex={zIndex || (isDropdown ? 50000 : undefined)}
        style={(zIndex || isDropdown) ? { position: 'relative' } : undefined}
      >
        <XStack flex={1}>
          <View style={{ backgroundColor: "transparent", maxWidth: 60, top: -5 }}>
            {item.content?.validation?.required &&
              <View style={{ maxWidth: 40, backgroundColor: "transparent" }}>
                {
                  fieldProps?.meta?.touched && !fieldProps.meta.error ?
                    <AnimatedLottieView source={require("@assets/animations/registerSuccess.json")} style={{ flex: 1 }} autoPlay />
                    :
                    <AnimatedLottieView colorFilters={[{
                      keypath: "layers.1.shapes.0.it.1.c.k", color: "red"
                    }]} source={require("@assets/animations/exclamation-mark.json")} style={{ flex: 1 }} autoPlay />
                }
              </View>
            }
          </View>
          {
            item.title !== 'avatar' && item.content?.type !== 'avatar' && !hideLabel && 
            <YStack marginBottom={5} width="100%">
              <Text flexWrap='wrap' textWrap='wrap' style={fieldStyles.typeStyle}>{item.content?.label || parseByDelimiter(item.title)}</Text>
            </YStack>
          }
        </XStack>
        <HandleFieldType
          blackText={blackText}
          setRef={(fieldName, ref) => setInputRef?.(fieldName, ref)}
          nextFieldFunction={(index, length) => { nextFieldFunction(allItems[index + 1]?.type, index, length) }}
          otherVals={otherVals}
          index={index}
          fieldProps={field}
          schemaItem={item}
          length={allItems?.length}
          readOnly={readOnly}
          inlineFields={inlineFields}
        />
      </YStack>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison function to prevent re-renders on every keystroke
    // Only re-render if item structure or error/touched state changes
    return (
      prevProps.item?.title === nextProps.item?.title &&
      prevProps.item?.key === nextProps.item?.key &&
      prevProps.item?.type === nextProps.item?.type &&
      prevProps.fieldProps?.meta?.touched === nextProps.fieldProps?.meta?.touched &&
      prevProps.fieldProps?.meta?.error === nextProps.fieldProps?.meta?.error &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.hideLabel === nextProps.hideLabel &&
      prevProps.blackText === nextProps.blackText
    );
  });

const Row = React.memo(({ blackText, otherVals, items, allItems, index: rowIndex, hideLabel, nextFieldFunction, setInputRef, column, totalRowsCount, readOnly = false, inlineFields = false }) => {
  const totalItemsInRow = items.length;

    return (
      <View style={{
        flex: 1,
        flexDirection: column ? 'column' : 'row',
        minWidth: inlineFields ? undefined : "100%",
        alignSelf: "stretch",
        width: inlineFields ? '100%' : undefined,
        gap: 25,
        padding: 5,
        backgroundColor: "transparent",
        // Set zIndex for the row: Earlier rows (lower rowIndex) get higher zIndex
        // This allows dropdowns in upper rows to expand over lower rows.
        zIndex: totalRowsCount !== undefined ? (totalRowsCount - rowIndex) : undefined,
        // Setting position: 'relative' ensures zIndex applies correctly and creates a stacking context.
        position: 'relative',
      }}>
        {items.map((item, index) => { // 'index' here is the index of the item within the current row
          // Base zIndex for item within the row: Earlier items (lower index, leftmost) get higher zIndex.
          // This makes sure a dropdown on the left appears over fields to its right within the same row.
          let itemZIndex = totalItemsInRow - index;

          // CRITICAL: Identify all dropdown types here.
          // This includes standard dropdowns and user selection dropdowns.
          const DROPDOWN_TYPES = [
            'select', 
            'picker', 
            'dropdown', 
            'dropDown',
            'combobox', 
            'date', 
            'time', 
            'datetime',
            'user_select',
            'user_stores',
            'userSelect',
            'userStores'
          ];
          
          // Check if item type is a dropdown
          const isDropdownType = DROPDOWN_TYPES.includes(item.type) || 
                                 DROPDOWN_TYPES.includes(item.content?.type) ||
                                 DROPDOWN_TYPES.includes(item.ref) ||
                                 DROPDOWN_TYPES.includes(item.key);
          
          // Check if ref/key indicates a dropdown (common pattern for user selection fields)
          const refOrKey = item.ref || item.key || '';
          const isDropdownByRef = refOrKey && (
            refOrKey.includes('user') || 
            refOrKey.includes('select') || 
            refOrKey.includes('dropdown') ||
            refOrKey.includes('stores')
          );

          const isDropdown = isDropdownType || isDropdownByRef;

          let dynamicZIndex = itemZIndex; // Default zIndex based on position in row

          if (isDropdown) {
            // For dropdowns, apply a very high base zIndex to ensure they appear on top of everything.
            // Earlier rows get higher zIndex so their dropdowns appear above fields in later rows.
            // Within the same row, later items (right side) get higher zIndex.
            const baseDropdownZIndex = 50000; // Very high base zIndex for all dropdowns
            // Earlier rows get higher offset: rowIndex 0 gets highest, rowIndex 1 gets less, etc.
            // This ensures dropdowns in row 0 appear above fields in row 1, row 2, etc.
            const rowOffset = (totalRowsCount !== undefined ? (totalRowsCount - rowIndex) : 0) * 1000;
            // Later items in same row get higher zIndex: index 0 = 0, index 1 = 1, index 2 = 2, etc.
            const reverseOrderOffset = index;
            dynamicZIndex = baseDropdownZIndex + rowOffset + reverseOrderOffset;
          }

          // Handle the 'phone' specific zIndex if it needs a very high fixed zIndex
          // and if it's not already classified as a dropdown type.
          if (Platform.OS === 'web' && item.type === 'phone' && !isDropdown) {
            dynamicZIndex = Math.max(dynamicZIndex, 1000); // Ensure 'phone' gets at least 1000
          }

          if (item.content?.reliesOn) {
            const fieldName = item.key || item.title;
            const htmlId = item.htmlId || fieldName;
            return (
              <Field key={htmlId} id={htmlId} name={fieldName}>
                {(fieldProps: FieldProps) => {
                  if (fieldProps.form.values[item.content.reliesOn] === item.content.reliesOnValue) {
                    return (
                      <Pressable
                        style={{
                          // Apply the calculated zIndex here
                          zIndex: dynamicZIndex,
                          borderWidth: 2,
                          alignSelf: "stretch",
                          flexDirection: "row",
                          flex: 1,
                          gap: 10,
                          position: 'relative', // Ensure position is set for zIndex to work
                        }}
                        disabled={Platform.OS === 'web'}
                        onPress={() => Keyboard.dismiss()}
                        hitSlop={10}
                      >
                        <View style={{ backgroundColor: "transparent", maxWidth: 60, top: 0 }}>
                          {
                            item.content?.validation?.required &&
                            <View style={{ maxWidth: 40, backgroundColor: "transparent" }}>
                              {
                                fieldProps.meta.touched && !fieldProps.meta.error ?
                                  <AnimatedLottieView source={require("@assets/animations/registerSuccess.json")} style={{ flex: 1 }} autoPlay />
                                  :
                                  <AnimatedLottieView colorFilters={[{
                                    keypath: "layers.1.shapes.0.it.1.c.k", color: "red"
                                  }]} source={require("@assets/animations/exclamation-mark.json")} style={{ flex: 1 }} autoPlay />
                              }
                            </View>
                          }
                        </View>
                        {
                          !hideLabel && !Platform.OS === 'web' &&
                          <Label style={fieldStyles.typeStyle} htmlFor={htmlId} color={blackText ? 'black' : 'white'}>{item.content?.label || parseByDelimiter(item.title)}</Label>
                        }
                        <View style={{
                          flex: Platform.OS === 'web' ? undefined : 1,
                          paddingLeft: 20,
                          backgroundColor: "transparent",
                          overflow: Platform.OS === 'web' ? 'visible' : 'hidden',
                          position: Platform.OS === 'web' ? 'relative' : undefined
                        }}>
                          <ErrorBoundary>
                            <GetField hideLabel={hideLabel} blackText={blackText} otherVals={otherVals} item={item} field={fieldProps.field} allItems={allItems} index={index} nextFieldFunction={nextFieldFunction} setInputRef={setInputRef} fieldProps={fieldProps} readOnly={readOnly} zIndex={dynamicZIndex} inlineFields={inlineFields} />
                            {
                              item.content?.parent && <Separator />
                            }
                          </ErrorBoundary>
                        </View>
                      </Pressable>
                    )

                  }
                }
                }
              </Field>
            )
          }
          if (item.type === "timeslider") {
            const fieldName = item.key || item.title;
            const htmlId = item.htmlId || fieldName;
            return (
              <FastField key={htmlId} name={fieldName}>
                {(fieldProps: FieldProps) => {
                  return (
                    <ErrorBoundary>
                      <View style={{
                        zIndex: dynamicZIndex, // Apply calculated zIndex here
                        position: 'relative', // Ensure position is set for zIndex to work
                      }}>
                        <GetField hideLabel={hideLabel} blackText={blackText} otherVals={otherVals} item={item} field={fieldProps.field} allItems={allItems} index={index} nextFieldFunction={nextFieldFunction} setInputRef={setInputRef} fieldProps={fieldProps} readOnly={readOnly} zIndex={dynamicZIndex} inlineFields={inlineFields} />
                        {
                          item.content?.parent && <Separator />
                        }
                      </View>
                    </ErrorBoundary>
                  )
                }}
              </FastField>
            )
          }
          else {
            const fieldName = item.key || item.title;
            const htmlId = item.htmlId || fieldName;
            return (
              <FastField key={htmlId} name={fieldName}>
                {(fieldProps: FieldProps) => {
                  return Platform.OS === 'web' ? (
                    <YStack
                      zIndex={dynamicZIndex} // Apply calculated zIndex here
                      style={{ position: (item.type === 'phone' || isDropdown) ? 'relative' : undefined }} // Ensure position is relative for zIndex
                    >

                      <View style={{
                        flex: Platform.OS === 'web' ? 0 : 1,
                        backgroundColor: "transparent",
                        overflow: Platform.OS === 'web' ? 'visible' : 'hidden',
                        position: Platform.OS === 'web' ? 'relative' : undefined
                      }}>
                        <ErrorBoundary>
                          <GetField fieldProps={fieldProps} hideLabel={hideLabel} blackText={blackText} otherVals={otherVals} item={item} field={fieldProps.field} allItems={allItems} index={index} nextFieldFunction={nextFieldFunction} setInputRef={setInputRef} readOnly={readOnly} zIndex={dynamicZIndex} inlineFields={inlineFields} />
                          {
                            item.content?.parent && <Separator />
                          }
                        </ErrorBoundary>
                      </View>
                    </YStack>
                  ) :
                    (
                      <Pressable style={{
                        alignSelf: "stretch",
                        flexDirection: "row",
                        gap: 10,
                        zIndex: dynamicZIndex, // Apply calculated zIndex here
                        position: 'relative', // Ensure position is set for zIndex to work
                      }}
                        disabled={item.type === "timeslider"}
                        onPress={() => Keyboard.dismiss()}
                        hitSlop={10}
                      >
                        <View style={{ position: "absolute", backgroundColor: "transparent", minWidth: "10%", maxWidth: 60, left: -10, top: 50 }}>
                          {
                            item.content?.validation?.required &&
                            <View style={{ maxWidth: 40, backgroundColor: "transparent" }}>
                              {
                                fieldProps.meta.touched && !fieldProps.meta.error ?
                                  <AnimatedLottieView source={require("@assets/animations/registerSuccess.json")} style={{ flex: 1 }} autoPlay />
                                  :
                                  <AnimatedLottieView colorFilters={[{
                                    keypath: "layers.1.shapes.0.it.1.c.k", color: "red"
                                  }]} source={require("@assets/animations/exclamation-mark.json")} style={{ flex: 1 }} autoPlay />
                              }
                            </View>
                          }
                        </View>
                        <View style={{
                          flex: Platform.OS === 'web' ? 0 : 1,
                          backgroundColor: "transparent",
                          overflow: Platform.OS === 'web' ? 'visible' : 'hidden',
                          position: Platform.OS === 'web' ? 'relative' : undefined
                        }}>
                          <ErrorBoundary>
                            <GetField hideLabel={hideLabel} blackText={blackText} otherVals={otherVals} item={item} field={fieldProps.field} allItems={allItems} index={index} nextFieldFunction={nextFieldFunction} setInputRef={setInputRef} readOnly={readOnly} zIndex={dynamicZIndex} inlineFields={inlineFields} />
                            {
                              item.content?.parent && <Separator />
                            }
                          </ErrorBoundary>
                        </View>
                      </Pressable>
                    )

                }}
              </FastField>
            )
          }
        })}
      </View>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders when items array reference changes but content is the same
    // Only re-render if actual item structure changes
    if (prevProps.items?.length !== nextProps.items?.length) return false;
    if (prevProps.rowIndex !== nextProps.rowIndex) return false;
    if (prevProps.totalRowsCount !== nextProps.totalRowsCount) return false;
    if (prevProps.readOnly !== nextProps.readOnly) return false;
    if (prevProps.hideLabel !== nextProps.hideLabel) return false;
    if (prevProps.blackText !== nextProps.blackText) return false;
    if (prevProps.column !== nextProps.column) return false;
    
    // Check if items have actually changed by comparing keys/titles
    const prevKeys = prevProps.items?.map(i => i.key || i.title).join(',') || '';
    const nextKeys = nextProps.items?.map(i => i.key || i.title).join(',') || '';
    if (prevKeys !== nextKeys) return false;
    
    return true; // Props are equal, skip re-render
  });

const fieldStyles = StyleSheet.create({
  typeStyle: {
    fontWeight: 'bold',
    paddingHorizontal: 15,
    height: 30,
    maxHeight: 30,
    left: -10,
    justifyContent: 'center',
      color: "rgb(51,51,51)",
      top: Platform.select({ web: -5, native: 10 })
    }
  })

export default Row;